import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { useLocalisation } from '@3plearning/lingui-cdn';

import OutsideClickHandler from '@helpers/hoc/close/outside-click-handler';
import useContextModal from '@helpers/hooks/use-context-modal';
import useCourseVersion from '@helpers/hooks/use-course-version';
import { getLocale } from '@helpers/locale';

import { activateModal, dismissModal } from '@containers/modal-manager/modal-manager-actions';
import { postPreviewAssignment } from '@containers/gateways/gateways-actions';

import ContextModal from '@components/context-modal';
import StandardInfo from '@components/standard-info';
import NeedHelp from '@components/need-help';
import { createAssignModal } from '@components/upf-assign-preview-modal/upf-assign-preview-modal';
import { showToast } from '@components/toast/toast-action';

import { filterNeedAttentionStudentsSelector } from './selectors';
import { getAssignmentPreviewUrl } from './helpers';
import getTranslations from './translations';
import HelpIcon from './help-icon';
import { Root, Name, Preview } from './styled';

const LearningJourney = props => {
  const { learningJourneyId } = props;

  /**
   * Store
   */
  const learningJourneys = useSelector(state => state.entitiesReducer.learningJourneys);
  const standards = useSelector(state => state.entitiesReducer.standards);
  const productId = useSelector(state => state.product.id);
  const needAttentionStudents = useSelector(filterNeedAttentionStudentsSelector(learningJourneyId));

  /**
   * Local variables
   */
  const {
    name: learningJourneyName,
    externalKey,
    description,
    childTopics: learningJourneyStepIds = [],
    standardId
  } = learningJourneys[learningJourneyId] || {};
  const { name: standardName } = standards[standardId] || {};
  const needAttentionStudentsCount = needAttentionStudents.length;

  /**
   * Hooks
   */
  const dispatch = useDispatch();
  const { i18n } = useLocalisation();
  const { showContextModal, closeContextMenu, targetRef, contentModalProps } = useContextModal();
  const {
    showContextModal: showNameContextModal,
    closeContextMenu: closeNameContextMenu,
    contentModalProps: nameContentModalProps
  } = useContextModal({ defaultTargetRef: targetRef });
  const { selectedCourseId, selectedCourseVersion } = useCourseVersion();

  /**
   * Translations
   */
  const { PREVIEW_TEXT, DEFAULT_PREVIEW_ERROR_TEXT } = getTranslations({ i18n });

  /**
   * Functions
   */
  const viewDemo = assignmentId => {
    const assignmentPreviewUrl = getAssignmentPreviewUrl(assignmentId);

    window.open(assignmentPreviewUrl, '_blank');
  };

  const handlePreview = async () => {
    dispatch(activateModal(createAssignModal(null, i18n, () => ({}), true)));

    try {
      const responseData = await dispatch(
        postPreviewAssignment({
          productId,
          courseId: selectedCourseId,
          courseVersion: selectedCourseVersion,
          locale: getLocale(),
          learningJourneyId
        })
      );

      dispatch(dismissModal());
      dispatch(activateModal(createAssignModal(responseData, i18n, () => viewDemo(responseData.assignmentId), false)));
    } catch (error) {
      const errorMessage = error.message || DEFAULT_PREVIEW_ERROR_TEXT;
      dispatch(dismissModal());
      dispatch(
        activateModal(
          createAssignModal(null, i18n, () => dispatch(showToast(errorMessage, 'error')), false, errorMessage)
        )
      );
    }
  };

  return (
    <Root data-testid="learning-journey" ref={targetRef} onClick={showContextModal}>
      <HelpIcon needAttentionStudentsCount={needAttentionStudentsCount} />

      <Name data-testid="learning-journey-name" onMouseEnter={showNameContextModal} onMouseLeave={closeNameContextMenu}>
        {learningJourneyName}
      </Name>

      <OutsideClickHandler callback={closeContextMenu} forButton={`showContextModal_${learningJourneyId}`}>
        <ContextModal {...contentModalProps}>
          {needAttentionStudentsCount > 0 && <NeedHelp i18n={i18n} helpSeekingStudents={needAttentionStudents} />}

          <Preview role="button" tabIndex="-1" onClick={handlePreview}>
            {PREVIEW_TEXT}
          </Preview>

          <StandardInfo
            count={learningJourneyStepIds.length}
            description={description}
            displayHideOption={false}
            externalKey={externalKey}
            i18n={i18n}
            ljName={learningJourneyName}
            standardName={standardName}
          />
        </ContextModal>

        <ContextModal {...nameContentModalProps} name={learningJourneyName} contextTheme="learningJourney" />
      </OutsideClickHandler>
    </Root>
  );
};

LearningJourney.propTypes = {
  learningJourneyId: PropTypes.number.isRequired
};

export default LearningJourney;
